import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, Stack, TextField, Button, Switch, MenuItem, Paper, Collapse, Divider, Accordion, AccordionSummary, AccordionDetails, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// TinyMCE WYSIWYG editor and sanitization for preview
import { Editor } from '@tinymce/tinymce-react';
import DOMPurify from 'dompurify';
// TinyMCE core + theme + common plugins (bundled locally)
import tinymce from 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce/plugins/link';
import 'tinymce/plugins/table';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/code';
import 'tinymce/plugins/autolink';
// Include TinyMCE skins to avoid runtime fetches
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';

// Ensure global availability for the React wrapper
if (typeof window !== 'undefined') {
  window.tinymce = window.tinymce || tinymce;
}

// Default/fallback event types - used if API fails or returns empty
const DEFAULT_EVENT_TYPES = [
  'Leave.NewRequest',
  'Leave.NewRequest.Confirmation',
  'Leave.PendingBoard',
  'Leave.Reminder',
  'Leave.CancelRequested',
  'Leave.CancelApproved',
  'Leave.CancelRejected',
  'Leave.CancelledBeforeApproval',
  'Leave.CancelledByRequester',
  'Leave.Approved',
  'Leave.Rejected',
  'Leave.SickLeave.Submitted',
  'ShortAbsence.NewRequest',
  'ShortAbsence.Approved',
  'ShortAbsence.Rejected',
  'CareerEvent.FollowUpReminder',
  'CareerEvent.FollowUpOverdue',
  'Equipment.NewRequest',
  'Equipment.PendingBoard',
  'Equipment.Approved',
  'Equipment.ReadyForOrder',
  'Equipment.Ordered',
  'Equipment.Fulfilled',
  'Equipment.Rejected'
];

const NotificationSettings = () => {
  const { t } = useTranslation();
  const [eventTypes, setEventTypes] = useState(DEFAULT_EVENT_TYPES);
  const [rules, setRules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [eventType, setEventType] = useState('Leave.NewRequest');
  const [audience, setAudience] = useState('requester');
  const [scopeDeptId, setScopeDeptId] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [isHtml, setIsHtml] = useState(true);
  const [isActiveTpl, setIsActiveTpl] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [msg, setMsg] = useState('');
  const [editingId, setEditingId] = useState(null);
  const editorRef = useRef(null);
  const plainRef = useRef(null);

  // Placeholders depend on event type category
  const LEAVE_PLACEHOLDERS = ['{EmployeeName}','{StartDate}','{EndDate}','{BusinessDays}','{Reason}','{CancelReason}','{RejectionReason}','{RequestId}','{FirstLevelApprover}','{ApproveUrl}','{RejectUrl}'];
  const CAREER_EVENT_PLACEHOLDERS = ['{EmployeeName}','{EventTitle}','{EventTypeName}','{FollowUpDescription}','{DueDate}','{DaysOverdue}','{AssignedToUserName}','{DashboardUrl}'];
  const EQUIPMENT_PLACEHOLDERS = ['{EmployeeName}','{DepartmentName}','{CategoryName}','{SubcategoryName}','{Quantity}','{Justification}','{Status}','{RequestDate}','{ApproverName}','{RejectionReason}','{Supplier}','{ActualPrice}','{RequestId}','{ApproveUrl}','{RejectUrl}'];

  const PLACEHOLDERS = eventType.startsWith('Equipment.') ? EQUIPMENT_PLACEHOLDERS
    : eventType.startsWith('CareerEvent.') ? CAREER_EVENT_PLACEHOLDERS
    : LEAVE_PLACEHOLDERS;

  const insertPlaceholder = (ph) => {
    if (isHtml && editorRef.current) {
      editorRef.current.insertContent(ph);
      return;
    }
    const el = plainRef.current;
    if (el) {
      const start = el.selectionStart ?? 0;
      const end = el.selectionEnd ?? start;
      const newVal = (body ?? '');
      const updated = newVal.slice(0, start) + ph + newVal.slice(end);
      setBody(updated);
      // restore caret
      setTimeout(() => {
        el.focus();
        const pos = start + ph.length;
        el.setSelectionRange?.(pos, pos);
      }, 0);
    }
  };


  // Load available event types from API (runs once on mount)
  const loadEventTypes = async () => {
    try {
      const types = await api.get('/notification-rules/event-types');
      if (types && types.length > 0) {
        // Merge with defaults to ensure we have all known types, then sort
        const merged = [...new Set([...DEFAULT_EVENT_TYPES, ...types])].sort();
        setEventTypes(merged);
      }
    } catch (err) {
      console.warn('Failed to load event types from API, using defaults:', err);
    }
  };

  useEffect(() => { loadEventTypes(); }, []);

  const load = async () => {
    const rs = await api.get(`/notification-rules?eventType=${encodeURIComponent(eventType)}`);
    setRules(rs || []);
    const ts = await api.get(`/email-templates?eventType=${encodeURIComponent(eventType)}`);
    setTemplates(ts || []);
  };

  useEffect(() => { load(); }, [eventType]);

  const addRule = async () => {
    await api.post('/notification-rules', { eventType, audience, scopeDepartmentId: scopeDeptId ? Number(scopeDeptId) : null, enabled });
    await load();
  };

  const addTemplate = async () => {
    // Simple client-side validation (allow pure HTML bodies)
    const subj = (subject || '').trim();
    const bodyOk = !!(body || '').trim();
    if (!subj || !bodyOk) {
      setMsg('Subject and body are required.');
      console.warn('Template validation failed:', { subject: subj, bodyLength: (body || '').length });
      return;
    }
    try {
      await api.post('/email-templates', { eventType, subject, body, isHtml, isActive: true });
      setSubject(''); setBody(''); setEditingId(null); setMsg('Template created successfully!');
      await load();
    } catch (err) {
      console.error('Failed to create template:', err);
      setMsg('Error creating template: ' + (err.message || 'Unknown error'));
    }
  };

  const startEdit = (tpl) => {
    setEditingId(tpl.id);
    setSubject(tpl.subject || '');
    setBody(tpl.body || '');
    setIsHtml(!!tpl.isHtml);
    setIsActiveTpl(!!tpl.isActive);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setSubject('');
    setBody('');
    setIsActiveTpl(true);
  };

  const updateTemplate = async () => {
    const tpl = (templates||[]).find(t=>t.id===editingId);
    if (!tpl) return;
    const subj = (subject || '').trim();
    const bodyOk = !!(body || '').trim();
    if (!subj || !bodyOk) { setMsg('Subject and body are required.'); return; }
    const payload = {
      id: tpl.id,
      eventType: tpl.eventType,
      locale: tpl.locale ?? null,
      subject,
      body,
      isHtml,
      isActive: isActiveTpl
    };


    await api.put(`/email-templates/${tpl.id}`, payload);
    setEditingId(null); setMsg('');
    await load();
  };

  const toggleActive = async (tpl, active) => {
    const payload = {
      id: tpl.id,
      eventType: tpl.eventType,
      locale: tpl.locale ?? null,
      subject: tpl.subject,
      body: tpl.body,
      isHtml: !!tpl.isHtml,
      isActive: !!active
    };
    await api.put(`/email-templates/${tpl.id}`, payload);
    await load();
  };


  const sendTest = async () => {
    if (!testTo) { setMsg(t('notifications.test.enterRecipient')); return; }
    try {
      setSendingTest(true);
      await api.post('/email-templates/test-send', { to: testTo, subject, body, isHtml });
      setMsg(t('notifications.test.sent'));
    } catch (e) {
      setMsg(t('notifications.test.error'));
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5">{t('notifications.title')}</Typography>

      <Box sx={{ display:'grid', gap:2, gridTemplateColumns:{ xs:'1fr', md:'1fr 1fr' }, alignItems:'start', mt:2 }}>
        <TextField select label={t('notifications.eventType')} value={eventType} onChange={e=>setEventType(e.target.value)} size="small" sx={{ minWidth: 260, justifySelf:'start' }}>
          {eventTypes.map(et => (
            <MenuItem key={et} value={et}>{et}</MenuItem>
          ))}
        </TextField>

        <Accordion defaultExpanded={false} sx={{ bgcolor: 'background.paper', border:'1px solid', borderColor:'divider', borderRadius:1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">{t('notifications.rules.title')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction={{ xs:'column', md:'row' }} spacing={2} sx={{ my: 1 }}>
              <TextField select label={t('notifications.rules.audience')} value={audience} onChange={e=>setAudience(e.target.value)} size="small" sx={{ minWidth: 200 }}>
                {['requester','approvers','admins','hr','board','manager','dm','om'].map(a => (
                  <MenuItem key={a} value={a}>{a}</MenuItem>
                ))}
              </TextField>
              <TextField label={t('notifications.rules.scopeDeptId')} value={scopeDeptId} onChange={e=>setScopeDeptId(e.target.value)} size="small" />
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>{t('notifications.rules.enabled')}</Typography>
                <Switch checked={enabled} onChange={e=>setEnabled(e.target.checked)} />
              </Stack>
              <Button variant="contained" onClick={addRule}>{t('notifications.rules.add')}</Button>
            </Stack>
            <Box>
              {rules.map(r => (
                <Box key={r.id} sx={{ p:1, borderBottom:'1px solid', borderColor:'divider', fontSize: 13 }}>
                  {r.eventType} · {r.audience} · {t('notifications.rules.scope')}: {r.scopeDepartmentId ?? 'global'} · {t('notifications.rules.enabled')}: {String(r.enabled)}
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Paper sx={{ p:2, mt:3 }}>
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap: 2 }}>
          <Typography variant="subtitle1">{t('notifications.templates.title')}</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">{t('notifications.templates.html')}</Typography>
            <Switch checked={isHtml} onChange={e=>setIsHtml(e.target.checked)} />
            <Button size="small" variant="text" onClick={()=>setShowHelp(v=>!v)}>{showHelp ? t('notifications.templates.hideHelp') : t('notifications.templates.howItWorks')}</Button>
          </Stack>
        </Box>

          <Collapse in={showHelp} unmountOnExit>
            <Box sx={{ mt: 1.5, p: 1.5, border: '1px dashed', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('notifications.templates.howItWorks')}</Typography>
              <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                <li>{t('notifications.templates.help.selectEvent')}</li>
                <li>{t('notifications.templates.help.fill')}</li>
                <li>{t('notifications.templates.help.placeholders')}</li>
                <li>{t('notifications.templates.help.html')}</li>
              </ul>
              <Typography variant="caption" sx={{ display:'block', mt: 1, color:'text.secondary' }}>
                {t('notifications.templates.help.whenSent')}
              </Typography>
              <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                <li><b>Leave.NewRequest</b> — {t('notifications.templates.help.events.newRequest')}</li>
                <li><b>Leave.NewRequest.Confirmation</b> — {t('notifications.templates.help.events.newRequestConf')}</li>
                <li><b>Leave.Reminder</b> — {t('notifications.templates.help.events.reminder')}</li>
                <li><b>Leave.CancelRequested</b> — {t('notifications.templates.help.events.cancelRequested')}</li>
                <li><b>Leave.CancelApproved</b> — {t('notifications.templates.help.events.cancelApproved')}</li>
                <li><b>Leave.CancelRejected</b> — {t('notifications.templates.help.events.cancelRejected')}</li>
                <li><b>Leave.CancelledBeforeApproval</b> — {t('notifications.templates.help.events.cancelledBeforeApproval')}</li>
                <li><b>Leave.CancelledByRequester</b> — {t('notifications.templates.help.events.cancelledByRequester')}</li>
              </ul>
              <Typography variant="subtitle2" sx={{ mt:2 }}>{t('notifications.templates.help.logicTitle')}</Typography>
              <ul style={{ margin: 0, paddingInlineStart: 20 }}>
                <li>{t('notifications.templates.help.logic.scope')}</li>
                <li>{t('notifications.templates.help.logic.managerEscalation')}</li>
                <li>{t('notifications.templates.help.logic.fallback')}</li>
                <li>{t('notifications.templates.help.logic.multiple')}</li>
              </ul>
            </Box>
          </Collapse>

          <Stack spacing={2} sx={{ my: 1 }}>
            <TextField label={t('notifications.templates.subject')} value={subject} onChange={e=>setSubject(e.target.value)} size="small" helperText={t('notifications.templates.subjectHelp')} />

            <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap', overflowX:'auto' }}>
              <Typography variant="caption" sx={{ color:'text.secondary', mr:1 }}>{t('notifications.templates.placeholdersTitle')}</Typography>
              {PLACEHOLDERS.map(ph => (
                <Chip key={ph} label={ph} size="small" onClick={()=>insertPlaceholder(ph)} sx={{ cursor:'pointer' }} />
              ))}
            </Box>

            <Stack direction={{ xs:'column', md:'row' }} spacing={2}>
              <Box sx={{ flex:1, minWidth:0 }}>
                {isHtml ? (
                  <Editor
                    tinymce={tinymce}
                    apiKey="no-api-key"
                    value={body}
                    onEditorChange={(c)=>setBody(c)}
                    onInit={(_evt, editor)=>{ editorRef.current = editor; }}
                    init={{
                      license_key: 'gpl',
                      skin: false,
                      content_css: false,
                      height: 300,
                      menubar: false,
                      plugins: 'autolink link lists table code',
                      toolbar: 'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link table | code',
                      content_style: 'body { font-family: Segoe UI, Arial, sans-serif; font-size: 13px; } table { border-collapse: collapse; }',
                    }}
                  />
                ) : (
                  <TextField inputRef={plainRef} label={t('notifications.templates.bodyText')} value={body} onChange={e=>setBody(e.target.value)} size="small" multiline minRows={10} />
                )}

              </Box>
              <Box role="region" aria-label={t('notifications.templates.previewAria')} sx={{ flex:1, minWidth:0, p:1, border:'1px solid', borderColor:'divider', borderRadius:1, bgcolor:'background.paper' }}>
                <Typography variant="subtitle2" sx={{ mb:1 }}>{t('notifications.templates.preview')}</Typography>
                {isHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body || '') }} />
                ) : (
                  <pre style={{ whiteSpace:'pre-wrap', margin:0 }}>{body}</pre>
                )}
              </Box>
            </Stack>

            {msg && (
              <Box sx={{ p: 1.5, mb: 1, borderRadius: 1, bgcolor: msg.includes('Error') || msg.includes('required') ? 'error.lighter' : 'success.lighter', border: '1px solid', borderColor: msg.includes('Error') || msg.includes('required') ? 'error.main' : 'success.main' }}>
                <Typography variant="body2" color={msg.includes('Error') || msg.includes('required') ? 'error.main' : 'success.main'}>{msg}</Typography>
              </Box>
            )}
            <Stack direction={{ xs:'column', md:'row' }} spacing={1} alignItems="center">
              <TextField label={t('notifications.templates.testTo')} value={testTo} onChange={e=>setTestTo(e.target.value)} size="small" sx={{ minWidth: 260 }} />
              <Button variant="outlined" onClick={sendTest} disabled={sendingTest}>{t('notifications.templates.sendTest')}</Button>
            </Stack>
            <Divider sx={{ my:1 }} />
            <Button variant="contained" onClick={editingId ? updateTemplate : addTemplate}>{editingId ? t('notifications.templates.update') : t('notifications.templates.createCustom')}</Button>
            {editingId && <Button sx={{ ml:1 }} onClick={cancelEdit}>{t('notifications.templates.cancelEdit')}</Button>}
          </Stack>

          <Box sx={{ mt: 2 }}>
            {templates.map((tpl) => (
              <Box key={tpl.id} sx={{ p:1, borderBottom:'1px solid', borderColor:'divider', fontSize: 13, display:'flex', justifyContent:'space-between', alignItems:'center', gap:1 }}>
                <span>{tpl.eventType} · {tpl.locale ?? 'default'} · {tpl.subject}</span>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption">Active</Typography>
                  <Switch size="small" checked={!!tpl.isActive} onChange={(e)=>toggleActive(tpl, e.target.checked)} />
                  <Button size="small" onClick={()=>startEdit(tpl)}>{t('notifications.templates.edit')}</Button>
                </Stack>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
  );
};

export default NotificationSettings;

